import { applyLogoFallback } from '@/utils/logoFallback';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import gradeGenerator from '@/utils/gradeGenerator';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import {
  BRBadge,
  SupplementalInfoBadge,
  IdentificationCheckBadge,
  ECertBadge,
  CreditReportBadge,
  AccountVerifiedBadge,
  GoGlobalBadge,
} from '@/components/UI/Badge';
import PageContainer from '@/components/Container/PageContainer';
import InnerPageTitle from '@/components/UI/InnerPageTitle';

import { IconBtn } from '../style';
import { Main, InformationWrapper, ProfileWrapper, Hr, DetailWrapper, InfoTitle, ContentWrapper } from './style';

import { getYellowPageDetail } from '@/api/auth.api';
import { Box, Flex, LoadingPage } from '@/layout';
import { convertLink, convertCategory, convertToCurrentLangData, convertTitle } from '@/utils/convert';
import { errorHandler } from '@/utils/toastHandler';
import useCurrentLang from '@/hooks/useCurrentLang';
import PATH from '@/router/path';
import useResize from '@/hooks/useResize';
import { Button } from '@/components/UI/Button';
import { theme } from '@/theme/Theme';
import { Text } from '@/components/UI/Typography/Text';
import useCountryDetail from '@/hooks/useCountryDetail';

// 樣板區分左側和右側樣板, 參考下方"Information"和"Profile"
const YellowPageDetail = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResize();
  const { id } = useParams();
  const location = useLocation();

  const [tab, setTab] = useState<boolean>(true);
  const [showData, setShowData] = useState<boolean>(false);
  const [data, setData] = useState<MemberProfile>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showVerifiedBadge, setShowVerifiedBadge] = useState(false);
  const [prevLink, setPrevLink] = useState('');
  const { isLogin } = useAppSelector(getAuthSelector);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!id) return;
    // if (location.pathname.includes('/professionalService') && location.pathname.replace('/professionalService/', '') !== '0') {
    //   getData(id!);
    //   setShowData(true);
    // } else {
    //   if (location.pathname.replace('/eDirectory/', '') !== '0') {
    //     getData(location.pathname.replace('/eDirectory/', ''));
    //     setShowData(true);
    //   }
    // }

    getData(id!);
    setShowData(true);
  }, []);

  const getData = async (id: string) => {
    try {
      setIsLoading(true);
      const res = (await getYellowPageDetail({ id: id })).data;
      if (res.success) {
        setData(res.data);
        checkShowVerifiedBadge(res.data);
      }
    } catch (error: any) {
      if (error.response.status === 404) {
        errorHandler(error.response.data.data[0]);
        if (location.pathname.includes('/professionalService')) {
          navigate('/professionalService');
        } else {
          navigate('/eDirectory');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 確認是否顯示徽章
  const checkShowVerifiedBadge = (data: MemberProfile) => {
    // 有子母公司的情況需子公司與母公司皆有驗證才將驗證badge顯示
    if (data.verification.accountVerified && data.childCompanies) {
      const childVerified = data.childCompanies.every((item) => item.verification.accountVerified);
      if (childVerified) {
        setShowVerifiedBadge(true);
      }
      return;
    }

    // 梅子公司的情況只需母公司有驗證就將驗證badge顯示
    if (data.verification.accountVerified) {
      setShowVerifiedBadge(true);
    }
  };

  return (
    <Main isTablet={isTablet} isMobile={isMobile} isLogin={isLogin}>
      <PageContainer
        title={
          <InnerPageTitle
            link={prevLink}
            subtitle={
              <Flex gridGap={'5px'}>
                {data?.acc_coname}
                {showVerifiedBadge && (
                  <Flex>
                    <AccountVerifiedBadge width={24} align={'center'} />
                  </Flex>
                )}
              </Flex>
            }
          />
        }
        centerContent={true}
      >
        <LoadingPage isLoading={isLoading} />

        {showData && data && (
          <ContentWrapper isTablet={isTablet}>
            {isTablet ? (
              <>
                <Flex
                  gridGap={'10px'}
                  margin={'0 auto 30px'}
                  padding={isMobile ? '0 15px' : '0'}
                  width={'100%'}
                  maxWidth={400}
                  justifyContent={'center'}
                >
                  <Button
                    onClick={() => setTab(!tab)}
                    type={'button'}
                    size={'half'}
                    variant={tab ? 'regular' : 'normal-invert'}
                  >
                    {t('info.information')}
                  </Button>
                  <Button
                    onClick={() => setTab(!tab)}
                    type={'button'}
                    size={'half'}
                    variant={!tab ? 'regular' : 'normal-invert'}
                  >
                    {t('info.profile')}
                  </Button>
                </Flex>
                {tab ? <Information data={data} /> : <Profile data={data} showVerifiedBadge={showVerifiedBadge} />}
              </>
            ) : (
              <>
                <Information data={data} />
                <Profile data={data} showVerifiedBadge={showVerifiedBadge} />
              </>
            )}
          </ContentWrapper>
        )}
      </PageContainer>
    </Main>
  );
};

// 左側的公司詳細資料
interface InformationProps {
  data: MemberProfile;
}

const Information = ({ data }: InformationProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResize();
  const lang = useCurrentLang();

  const checkWebsiteLink = (link: string) => {
    if (link.includes('http://') || link.includes('https://')) {
      return link;
    } else {
      return 'http://' + link;
    }
  };

  const convertOptions = (options: string[]) => {
    function convertSector() {
      const sectorOptions = t('mapOptions.lawFirmSectorSpecialty', { returnObjects: true });
      // 檢查傳入的options是否有在sectorOptions的value裡面
      return options.map((option) => {
        const find = sectorOptions.find((item) => item.value === option);
        return find ? find.text : option;
      });
    }

    function convertPractice() {
      const practiceOptions = t('mapOptions.practiceAreas', { returnObjects: true });
      return options.map((option) => {
        const find = practiceOptions.find((item) => item.value === option);
        return find ? find.text : option;
      });
    }

    return { sector: convertSector().join(', '), practice: convertPractice().join(', ') };
  };

  return (
    <Flex flexDirection={'column'} width={isTablet ? '100%' : '70%'} gridGap={'20px'}>
      <InformationWrapper isMobile={isMobile} isTablet={isTablet}>
        <InfoTitle>{t('companyRepresentative')}</InfoTitle>
        <Flex flexDirection={'column'} gridGap={'15px'}>
          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.companyLogo')}</div>
            <div className="info">
              <img
                width={'100%'}
                height={100}
                style={{ objectFit: 'contain' }}
                src={data.acc_logo ?? '/images/default_company_logo.jpg'}
                onError={(event) => applyLogoFallback(event.currentTarget)}
                alt=""
              />
            </div>
          </DetailWrapper>

          {isTablet &&
            data.acc_category &&
            data.acc_category.replace(/\s+/g, '').toLowerCase().includes('1-hourfreelegal'.toLowerCase()) && (
              <img
                style={{ width: '70%', margin: 'auto' }}
                src="/images/yellowpage/1hLegalConsultation.png"
                alt="1 Hour Legal Consultation"
              />
            )}

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.website')}</div>
            <div className="info">
              {data.acc_website ? (
                <a
                  href={checkWebsiteLink(data.acc_website)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'underline', color: 'blue' }}
                >
                  {data.acc_website}
                </a>
              ) : (
                '-'
              )}
            </div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.companyEmail')}</div>
            <div className="info">
              {data.com_email ? (
                <a
                  href={`mailto: ${data.com_email}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'underline', color: 'blue' }}
                >
                  {data.com_email}
                </a>
              ) : (
                '-'
              )}
            </div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.telephone')}</div>
            <div className="info">{`(${data.acc_MobileCountryCode}) ${data.acc_tel}`}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.fax')}</div>
            <div className="info">{data.acc_fax ? `(${data.acc_MobileCountryCode}) ${data.acc_fax}` : '-'}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.companyAddress')}</div>
            <div className="info">{data.acc_address?.split('@@_@@').join(' ')}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.companyDescription')}</div>
            <div className="info">{data.acc_desc}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.natureOfBusiness')}</div>
            <div className="info">
              {data.acc_category
                ? `${convertCategory(data.acc_category, lang)} ${data.acc_category_other ? `(${data.acc_category_other})` : ''
                }`
                : '-'}
            </div>
          </DetailWrapper>

          {data.acc_category?.includes('Legal Services') && (
            <>
              {data.law_firm_sector_specialty.length !== 0 && (
                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.lawFirmSectorSpecialty')}</div>
                  <div className="info">{convertOptions(data.law_firm_sector_specialty).sector}</div>
                </DetailWrapper>
              )}
              {data.practice_areas.length !== 0 && (
                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.lawFirmPracticeAreas')}</div>
                  <div className="info">{convertOptions(data.practice_areas).practice}</div>
                </DetailWrapper>
              )}
            </>
          )}

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.languages')}</div>
            <div className="info">
              {data.acc_languages
                ? `${convertToCurrentLangData(data.acc_languages, 'language', lang)} ${data.acc_languages_other ? `(${data.acc_languages_other})` : ''
                }`
                : '-'}
            </div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.currency')}</div>
            <div className="info">
              {data.acc_currency
                ? `${convertToCurrentLangData(data.acc_currency, 'currency', lang)} ${data.acc_currency_other ? `(${data.acc_currency_other})` : ''
                } `
                : '-'}
            </div>
          </DetailWrapper>
        </Flex>
        <Hr />
        <InfoTitle>{t('companyRepresentativeInformation')}</InfoTitle>
        <Flex flexDirection={'column'} gridGap={'15px'}>
          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.companyName')}</div>
            <div className="info">{data.acc_coname}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.title')}</div>
            <div className="info">{convertTitle(data.acc_title, lang)}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.giveName')}</div>
            <div className="info">{data?.acc_firstName}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.familyName')}</div>
            <div className="info">{data.acc_lastName}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.email')}</div>
            <div className="info">
              {/* 點擊後打開信箱 */}
              <a
                href={`mailto: ${data.email}`}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'underline', color: 'blue' }}
              >
                {data.email}
              </a>
            </div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.jobPosition')}</div>
            <div className="info">{data.acc_jobPosition}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.region')}</div>
            <div className="info">
              {data.acc_country_other
                ? data.acc_country_other
                : convertToCurrentLangData(data.acc_country, 'name', lang)}
            </div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.timezone')}</div>
            <div className="info">{data.acc_timezone}</div>
          </DetailWrapper>

          <DetailWrapper isMobile={isMobile}>
            <div className="title">{t('info.mobilePhoneNumber')}</div>
            <div className="info">
              ({data.acc_countryCode}) {data.acc_mobile}
            </div>
          </DetailWrapper>
        </Flex>
        {/* 新增子公司資料顯示 */}
        {data.childCompanies &&
          Array.isArray(data.childCompanies) &&
          data.childCompanies.length !== 0 &&
          data.childCompanies.map((childCompany) => (
            <>
              <Hr />
              <InfoTitle>{t('companyRepresentativeInformation')}</InfoTitle>
              <Flex flexDirection={'column'} gridGap={'15px'}>
                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.companyName')}</div>
                  <div className="info">{childCompany.acc_coname}</div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.title')}</div>
                  <div className="info">{convertTitle(childCompany.acc_title, lang)}</div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.giveName')}</div>
                  <div className="info">{childCompany?.acc_firstName}</div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.familyName')}</div>
                  <div className="info">{childCompany.acc_lastName}</div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.email')}</div>
                  <div className="info">
                    {/* 點擊後打開信箱 */}
                    <a
                      href={`mailto: ${childCompany.email}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'underline', color: 'blue' }}
                    >
                      {childCompany.email}
                    </a>
                  </div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.jobPosition')}</div>
                  <div className="info">{childCompany.acc_jobPosition}</div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.region')}</div>
                  <div className="info">
                    {childCompany.acc_country_other
                      ? childCompany.acc_country_other
                      : convertToCurrentLangData(childCompany.acc_country, 'name', lang)}
                  </div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.timezone')}</div>
                  <div className="info">{childCompany.acc_timezone}</div>
                </DetailWrapper>

                <DetailWrapper isMobile={isMobile}>
                  <div className="title">{t('info.mobilePhoneNumber')}</div>
                  <div className="info">
                    ({childCompany.acc_countryCode}) {childCompany.acc_mobile}
                  </div>
                </DetailWrapper>
              </Flex>
            </>
          ))}
      </InformationWrapper>

      {data.acc_description && (
        <InformationWrapper isMobile={isMobile} isTablet={isTablet}>
          <div
            className="content"
            dangerouslySetInnerHTML={{
              __html: data.acc_description,
            }}
          />
        </InformationWrapper>
      )}
    </Flex>
  );
};

// 右側的公司連絡資料
interface ProfileProps {
  data: MemberProfile;
  showVerifiedBadge: boolean;
}

const Profile = ({ data, showVerifiedBadge }: ProfileProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResize();
  const navigate = useNavigate();
  const { userData } = useAppSelector(getAuthSelector);
  const lang = useCurrentLang();

  const goGlobalData = data.go_global_application;

  const firmSizeOptions = t('form.firmSize.options', { returnObjects: true });
  const crossBorderExpertiseOptions = t('form.crossBorderExpertise.options', { returnObjects: true });
  const countryOptions = useCountryDetail('name');
  const languagesOptions = useCountryDetail('languages');

  const badgeGenerater = (badgeAry: Verification) => {
    const badges: React.ReactNode[] = [];
    // badgeAry.creditReport && badges.push(<CreditReportBadge key={'creditReport'} width={24} align={'center'} />);
    badgeAry.eCert && badges.push(<ECertBadge key={'eCert'} width={24} align={'center'} />);
    badgeAry.identification &&
      badges.push(<IdentificationCheckBadge key={'identification'} width={24} align={'center'} />);
    badgeAry.person && badges.push(<BRBadge key={'person'} width={24} align={'center'} />);
    badgeAry.supplemental && badges.push(<SupplementalInfoBadge key={'supplemental'} width={24} align={'center'} />);
    showVerifiedBadge && badges.push(<AccountVerifiedBadge key={'accountVerified'} width={24} align={'center'} />);

    return badges;
  };

  const goToChat = (user_id: number) => {
    if (!user_id) return;
    navigate('/chat/start/' + user_id);
  };


  // 根據語系調整顯示的選項中英文版本
  const convertFirmSize = (value: string) => {
    const find = firmSizeOptions.find((item) => item.value === value);
    return find ? find.text : value;
  }

  const convertMarket = (value: string) => {
    const find = countryOptions.find((item) => item.value === value);
    return find ? find.text : value;
  }

  const convertLanguage = (value: string) => {
    // 此處為字串, 需打散後在查詢, 將陣列中全部替換後再重組回字串並返回
    const values = value.split(',').map((v) => v.trim());
    const convertedValues = values.map((v) => {
      const find = languagesOptions.find((item) => item.value === v);
      return find ? find.text : v;
    });
    return convertedValues.join(', ');
  }

  const convertService = (value: string) => {
    const find = crossBorderExpertiseOptions.find((item) => item.value === value);
    return find ? find.text : value;
  }

  return (
    <Flex width={isTablet ? '100%' : '35%'} flexDirection={'column'} gridGap={'10px'}>
      <ProfileWrapper isMobile={isMobile} isTablet={isTablet}>
        <div className="logoBox">
          <img
            width={'100%'}
            height={100}
            style={{ objectFit: 'contain' }}
            src={data.acc_logo ?? '/images/default_company_logo.jpg'}
            onError={(event) => applyLogoFallback(event.currentTarget)}
            alt=""
          />
        </div>
        <Flex marginTop={'15px'} gridGap={8}>
          {badgeGenerater(data.verification)}
        </Flex>
        {/* <Hr />
      <Flex alignItems={'center'}>
        {gradeGenerator(data.stars).map((item, index) => (
          <img width="33px" key={index} src={item} alt="grade" />
        ))}
        <Link to={'comments'}>{` (${data.rating_count})`}</Link>
      </Flex> */}
        <Hr />
        <Flex gridGap={10} marginTop={15} flexWrap={'wrap'}>
          {data?.id !== userData.id ? (
            <IconBtn onClick={() => goToChat(data?.id)} style={{ height: '44px', flex: isMobile ? '1' : 'unset' }}>
              <img src="/images/yellowpage/chat.svg" alt="" />
              {t('info.chat')}
            </IconBtn>
          ) : (
            <IconBtn className="disable">
              <img src="/images/yellowpage/chat.svg" alt="" />
            </IconBtn>
          )}
          {data.com_email && data.com_email !== '' && (
            <a href={`mailto: ${data.com_email}`} style={{ flex: isMobile ? '1' : 'unset' }}>
              <IconBtn style={{ width: isMobile ? '100%' : 'unset' }}>
                <img src="/images/yellowpage/mail-white.svg" alt="" />
                {t('info.email')}
              </IconBtn>
            </a>
          )}
          {/* <Link to={PATH.creditReport} style={{ width: isMobile ? '100%' : 'auto' }}>
          <Button
            width={isMobile ? '100%' : 'auto'}
            style={{ fontWeight: 400, padding: '0 10px', fontSize: theme.fontSizes.sm }}
            type={'button'}
            size={'half'}
            variant={'primary'}
          >
            {t('buttons.buyCreditReport')}
          </Button>
        </Link> */}
          {!isTablet &&
            data.acc_category &&
            data.acc_category.replace(/\s+/g, '').toLowerCase().includes('1-hourfreelegal'.toLowerCase()) && (
              <img
                style={{ width: '100%' }}
                src="/images/yellowpage/1hLegalConsultation.png"
                alt="1 Hour Legal Consultation"
              />
            )}
        </Flex>
      </ProfileWrapper>
      {
        goGlobalData && (
          <ProfileWrapper isMobile={isMobile} isTablet={isTablet}>
            <InfoTitle>{t('pages.yellowPage.detail.goGlobalExpertInformation')}</InfoTitle>
            <Box style={{ position: 'absolute', top: 15, right: 15 }}>
              <GoGlobalBadge width={77} align={'center'} tier={data.go_global_badge_tier} />
            </Box>

            <Flex gridGap={15} marginTop={15} flexWrap={'wrap'}>
              <Flex flexDirection={'column'} gridGap={10} width={'100%'}>
                <Text fontSize={theme.fontSizes.base} fontWeight={'bold'}>
                  {t('pages.yellowPage.detail.firmSize')}
                </Text>
                <Text color={theme.colors.gray400}>
                  {
                    convertFirmSize(goGlobalData.firm_size)
                  }
                </Text>
              </Flex>
              <Flex flexDirection={'column'} gridGap={10} width={'100%'}>
                <Text fontSize={theme.fontSizes.base} fontWeight={'bold'}>
                  {t('pages.yellowPage.detail.overseasNetwork')}
                </Text>
                <Text color={theme.colors.gray400}>
                  {goGlobalData.overseas_network_presence}
                </Text>
              </Flex>
              <Flex flexDirection={'column'} gridGap={10} width={'100%'}>
                <Text fontSize={theme.fontSizes.base} fontWeight={'bold'}>
                  {t('pages.yellowPage.detail.keyOverseasMarkets')}
                </Text>
                <Flex gridGap={8} flexWrap={'wrap'}>
                  {
                    goGlobalData.key_overseas_markets.map((market, index) => (
                      <Box key={index} background={'#E9EFFF'} padding={'8px 10px'}>
                        <Text key={index} color={theme.colors.gray400}>
                          {convertMarket(market)}
                        </Text>
                      </Box>
                    ))
                  }
                </Flex>
              </Flex>
              <Flex flexDirection={'column'} gridGap={10} width={'100%'}>
                <Text fontSize={theme.fontSizes.base} fontWeight={'bold'}>
                  {t('pages.yellowPage.detail.languages')}
                </Text>
                <Text color={theme.colors.gray400}>
                  {convertLanguage(goGlobalData.languages)}
                </Text>
              </Flex>
              <Flex flexDirection={'column'} gridGap={10} width={'100%'}>
                <Text fontSize={theme.fontSizes.base} fontWeight={'bold'}>
                  {t('pages.yellowPage.detail.crossBorderExpertiseServices')}
                </Text>
                <ul style={{ color: theme.colors.gray400, paddingLeft: '20px', listStyleType: 'disc' }}>
                  {
                    goGlobalData.cross_border_expertise && goGlobalData.cross_border_expertise.map((service, index) => (
                      <li key={index}>
                        <Text color={theme.colors.gray400}>
                          {convertService(service)}
                        </Text>
                      </li>
                    ))
                  }
                </ul>
              </Flex>
              <Flex flexDirection={'column'} gridGap={10} width={'100%'}>
                <Text fontSize={theme.fontSizes.base} fontWeight={'bold'}>
                  {t('pages.yellowPage.detail.successfulCaseRecords')}
                </Text>
                <Box background={'#E9EFFF'} padding={'8px 10px'}>
                  <ul style={{ color: theme.colors.gray400, paddingLeft: '20px', listStyleType: 'disc' }}>
                    {
                      goGlobalData.successful_case_records_data && goGlobalData.successful_case_records_data.length !== 0 && goGlobalData.successful_case_records_data.map((record, index) => (
                        <li key={index}>
                          <Text color={theme.colors.gray400}>
                            {lang === 'en' ? record.en : record.zh}
                          </Text>
                        </li>
                      ))
                    }
                  </ul>
                </Box>
              </Flex>
            </Flex>
          </ProfileWrapper>
        )
      }
    </Flex>
  );
};

export default YellowPageDetail;
